/**
 * products.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

// homepage/src/data/products.js

import {
  getWholesalePriceCents,
  getRetailPriceCents,
  getWholesalePricing,
} from '../utils/wholesalePricing';

export const products = [
  {
    id: 'book-1',
    type: 'Book',
    name: 'The Fiddler\'s Fakebook',
    author: 'David Kaynor',
    price: 2499, // in cents - retail price
    wholesalePricing: {
      standard: { price: 1999, minQuantity: 5 },    // 20% discount
      premium: { price: 1749, minQuantity: 10 },    // 30% discount
      volume: { price: 1499, minQuantity: 25 }      // 40% discount
    },
    image: '/images/fiddlers-fakebook.jpg',
    description: 'The ultimate resource for folk fiddlers. Contains nearly 500 tunes.',
    inventory: 15,
  },
  {
    id: 'book-2',
    type: 'Book',
    name: 'Irish Fiddle Solos',
    author: 'Pete Cooper',
    price: 1999,
    wholesalePricing: {
      standard: { price: 1599, minQuantity: 5 },    // 20% discount
      premium: { price: 1399, minQuantity: 10 },    // 30% discount
      volume: { price: 1199, minQuantity: 25 }      // 40% discount
    },
    image: '/images/irish-fiddle-solos.jpg',
    description: '64 classic tunes for the solo fiddler, with guitar chords.',
    inventory: 25,
  },
  {
    id: 'cd-1',
    type: 'CD',
    name: 'The High Lonesome Sound',
    artist: 'Bill Monroe',
    price: 1499,
    wholesalePricing: {
      standard: { price: 1199, minQuantity: 5 },    // 20% discount
      premium: { price: 1049, minQuantity: 10 },    // 30% discount
      volume: { price: 899, minQuantity: 25 }       // 40% discount
    },
    image: '/images/high-lonesome-sound.jpg',
    description: 'The foundational sound of bluegrass fiddle from the master himself.',
    inventory: 30,
  },
  {
    id: 'cd-2',
    type: 'CD',
    name: 'Now That\'s What I Call Fiddlin\'',
    artist: 'Various Artists',
    price: 1299,
    wholesalePricing: {
      standard: { price: 1039, minQuantity: 5 },    // 20% discount
      premium: { price: 909, minQuantity: 10 },     // 30% discount
      volume: { price: 779, minQuantity: 25 }       // 40% discount
    },
    image: '/images/now-thats-fiddlin.jpg',
    description: 'A compilation of the greatest fiddle tracks of all time.',
    inventory: 50,
  },
  {
    id: 'book-3',
    type: 'Book',
    name: 'Scottish Fiddle Tunes',
    author: 'J. Scott Skinner',
    price: 2299,
    wholesalePricing: {
      standard: { price: 1839, minQuantity: 5 },    // 20% discount
      premium: { price: 1609, minQuantity: 10 },    // 30% discount
      volume: { price: 1379, minQuantity: 25 }      // 40% discount
    },
    image: '/images/scottish-fiddle-tunes.jpg',
    description: 'Classic Scottish fiddle repertoire from the master composer.',
    inventory: 20,
  },
  {
    id: 'cd-3',
    type: 'CD',
    name: 'Cape Breton Fiddle Collection',
    artist: 'Various Artists',
    price: 1599,
    wholesalePricing: {
      standard: { price: 1279, minQuantity: 5 },    // 20% discount
      premium: { price: 1119, minQuantity: 10 },    // 30% discount
      volume: { price: 959, minQuantity: 25 }       // 40% discount
    },
    image: '/images/cape-breton-collection.jpg',
    description: 'Traditional Cape Breton fiddle music from the heart of Nova Scotia.',
    inventory: 35,
  }
];

export const getWholesalePrice = (product, userTier = 'standard', quantity = 1) => {
  const retail = getRetailPriceCents(product);
  const price = getWholesalePriceCents(product, userTier, quantity);
  return price === retail && !getWholesalePricing(product) ? (product.price ?? retail) : price;
};

export const getAvailableWholesaleTiers = (product, quantity = 1) => {
  const wp = getWholesalePricing(product);
  if (!wp) return [];

  const retail = getRetailPriceCents(product);

  return Object.entries(wp)
    .map(([tier, pricing]) => {
      const minQuantity = Number(pricing.minQuantity ?? pricing.min_qty ?? 1);
      const price = pricing.price != null
        ? Number(pricing.price)
        : (pricing.discount != null
          ? Math.round(retail * (1 - Number(pricing.discount) / 100))
          : retail);
      return { tier, price, minQuantity };
    })
    .filter(({ minQuantity }) => quantity >= minQuantity)
    .map(({ tier, price, minQuantity }) => ({
      tier,
      price,
      minQuantity,
      savings: retail - price,
      savingsPercent: retail > 0 ? Math.round(((retail - price) / retail) * 100) : 0,
    }));
};

export const getBestWholesalePrice = (product, userTier = 'standard', quantity = 1) => {
  const availableTiers = getAvailableWholesaleTiers(product, quantity);
  if (availableTiers.length === 0) return getRetailPriceCents(product);

  const userTierPricing = availableTiers.find((tier) => tier.tier === userTier);
  if (userTierPricing) return userTierPricing.price;

  return Math.min(...availableTiers.map((tier) => tier.price));
}; 